import './style.scss';

/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import Helmet from 'react-helmet';
import * as _ from 'lodash';

import { FormattedMessage, useIntl } from 'react-intl';
import { Table, Loader, Confirm, Form, Button, Icon } from 'semantic-ui-react';
import { browserHistory } from 'react-router';
import AnimalSearch from 'app/components/AnimalSearch';
import { API } from 'app/utils/apiCalls';
import { loadStudy } from 'app/utils/studyUtils';
import { isWorkListImage } from 'app/utils/isWorkListImage';
import StudyRow from 'app/containers/MonitoringPage/components/StudyRow';
import PreviewArea from 'app/containers/MonitoringPage/components/PreviewArea';
import useFilterStudiesInfiniteQuery from 'app/queries/useFilterStudiesInfiniteQuery';
import useDeleteStudyMutation from 'app/queries/useDeleteStudyMutation';
import PACSSyncAllButton from 'app/containers/MonitoringPage/components/PACSSyncAllButton';
import { useSyncAllStudiesFromStoreWithPACSMutationState } from 'app/queries/useSyncAllStudiesFromStoreWithPACSMutation';

const MonitoringPage: React.FC<{}> = () => {
  const intl = useIntl();
  const [thresholdForDisplay] = useState<number>(0.5);
  const [animalId, setAnimalId] = useState<string | undefined>(undefined);
  const [ownerId, setOwnerId] = useState<string | undefined>(undefined);
  const [fileId, setFileId] = useState<string | undefined>(undefined);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [regions, setRegions] = useState<string[]>([]);
  const [isPacsSynced, setIsPACSSynced] = useState<boolean | undefined>(undefined);
  const [studySelectedForRemoving, setStudySelectedForRemoving] = useState<string[]>([]);
  const [isAlertDeleteStudyOpen, setIsAlertDeleteStudyOpen] = useState<boolean>(false);

  const { status: syncStatus, variables } =
    _.last(useSyncAllStudiesFromStoreWithPACSMutationState()) ?? {};

  // In case synchronization is ongoing, we use the filters provided by the sync mutation
  const filters: API.StudiesFilter = {
    animalId,
    ownerId,
    fileId,
    startDate,
    endDate,
    regions,
  };

  useEffect(() => {
    if (syncStatus === 'pending') {
      setIsPACSSynced(false);
      setAnimalId(variables.filters?.animalId);
      setOwnerId(variables.filters?.ownerId);
      setFileId(variables.filters?.fileId);
      setStartDate(variables.filters?.startDate);
      setEndDate(variables.filters?.endDate);
      setRegions(variables.filters?.regions || []);
    }
  }, [syncStatus, variables]);

  const {
    data: studiesData,
    fetchNextPage,
    hasNextPage,
  } = useFilterStudiesInfiniteQuery({
    ...filters,
    isPacsSynced,
  });

  const studies = useMemo(
    () =>
      _.filter(_.flatten(studiesData?.pages || []), (study) =>
        study.images.some((image) => !isWorkListImage(image))
      ),
    [studiesData]
  );
  const [currentStudy, setCurrentStudy] = useState<API.Study | undefined>(undefined);
  const [currentStudyID, setCurrentStudyID] = useState<string | undefined>(undefined);
  const { mutateAsync: deleteStudyCall } = useDeleteStudyMutation();

  const scrollParentRef = useRef<HTMLDivElement | null>(null);

  const [multiSelectedStudies, setMultiSelectedStudies] = useState<string[]>([]);
  const [prevStudies, setPrevStudies] = useState(studies);

  if (studies !== prevStudies && studiesData !== undefined) {
    setPrevStudies(studies);
    const filteredMultiSelectedStudies = _.intersection(
      multiSelectedStudies,
      studies.map((study) => study._id)
    );
    setMultiSelectedStudies(filteredMultiSelectedStudies);
  }

  // TODO: Move this part of the code inside PreviewArea with a good React-query caching strategy
  const loadStudyMemoized = useCallback(
    _.memoize((studyID: string) => loadStudy([studyID], intl) as Promise<API.Study>),
    [intl]
  );

  const handleSelectRow = useCallback(
    (studyID: string) => {
      loadStudyMemoized(studyID).then(
        (loadedStudy) => setCurrentStudy(loadedStudy),
        (error) => {
          console.warn(error);
          loadStudyMemoized.cache.delete(studyID);
        }
      );
      setCurrentStudyID(studyID);
    },
    [loadStudyMemoized]
  );

  const setRegionsHandler = useCallback((regionsData: { value: string }[]) => {
    const selectedRegions: string[] = [];
    regionsData.forEach((region) => {
      if (region.value === 'thorax') selectedRegions.push('thorax_profil', 'thorax_face');
      else if (region.value === 'pelvis') selectedRegions.push('pelvis_profil', 'pelvis_face');
      else if (region.value === 'abdomen') selectedRegions.push('abdomen_profil', 'abdomen_face');
      else if (region.value === 'autre') selectedRegions.push('autre_autre');
    });
    setRegions(selectedRegions);
  }, []);

  const handleSelectStudyForRemoving = useCallback((studyID: string) => {
    setStudySelectedForRemoving([studyID]);
    setIsAlertDeleteStudyOpen(true);
  }, []);

  const deleteSelectedStudy = useCallback(() => {
    setIsAlertDeleteStudyOpen(false);
    deleteStudyCall({ studyIDs: studySelectedForRemoving }).then(() => {
      setCurrentStudyID(undefined);
    });
  }, [deleteStudyCall, studySelectedForRemoving]);

  const handleCancelDeleteStudy = useCallback(() => setIsAlertDeleteStudyOpen(false), []);

  const openMultiStudies = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (multiSelectedStudies.length === 0) return;

      browserHistory.push(
        `/viewer?${multiSelectedStudies.map((id) => `study_ids[]=${id}`).join('&')}`
      );
    },
    [multiSelectedStudies]
  );

  useEffect(() => {
    loadStudyMemoized.cache = new _.memoize.Cache();
  }, [loadStudyMemoized]);

  return (
    <div className=" monitoring-background dark-mode">
      <Helmet title={intl.formatMessage({ id: 'monitoring.pageTitle' })} />
      <div className="search-form">
        <label style={{ color: 'white' }}>
          <FormattedMessage id="monitoring.animalSearch" />
        </label>
        <div>
          <Form className="filters">
            <AnimalSearch
              setAnimalId={setAnimalId}
              setOwnerId={setOwnerId}
              setFileId={setFileId}
              onChooseStartDate={setStartDate}
              onChooseEndDate={setEndDate}
              startDate={startDate}
              endDate={endDate}
              setRegions={setRegionsHandler}
              disabled={syncStatus === 'pending'}
            />
          </Form>
        </div>
      </div>
      <div className="monitoring-table" ref={scrollParentRef}>
        <InfiniteScroll
          loadMore={() => fetchNextPage({ cancelRefetch: true })}
          hasMore={hasNextPage}
          loader={<Loader key={-1} active inline="centered" />}
          useWindow={false}
          getScrollParent={() => scrollParentRef.current}
        >
          <Table collapsing={false} inverted className="table-colors">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell className="multi-select-header-cell">
                  <div className="actions__icon">
                    <Icon
                      className="eye icon picoxia-fix"
                      title={intl.formatMessage({ id: 'monitoring.study.compare_multiple' })}
                      aria-label={intl.formatMessage({ id: 'monitoring.study.compare_multiple' })}
                      onClick={openMultiStudies}
                    />
                  </div>
                </Table.HeaderCell>
                <Table.HeaderCell style={{ width: '10%' }}>
                  <FormattedMessage id="monitoring.date" />
                </Table.HeaderCell>
                <Table.HeaderCell style={{ width: '8%' }}>
                  <FormattedMessage id="monitoring.id" />
                </Table.HeaderCell>
                <Table.HeaderCell style={{ width: '8%' }}>
                  <FormattedMessage id="monitoring.owner" />
                </Table.HeaderCell>
                <Table.HeaderCell style={{ width: '8%' }}>
                  <FormattedMessage id="monitoring.animal" />
                </Table.HeaderCell>
                <Table.HeaderCell style={{ width: '5%' }}>
                  <FormattedMessage id="monitoring.age" />
                </Table.HeaderCell>
                <Table.HeaderCell style={{ width: '5%' }}>
                  <FormattedMessage id="monitoring.sex" />
                </Table.HeaderCell>
                <Table.HeaderCell style={{ width: '5%' }}>
                  <FormattedMessage id="monitoring.images" />
                </Table.HeaderCell>
                <Table.HeaderCell style={{ width: '39%' }}>
                  <FormattedMessage id="monitoring.patterns" />
                </Table.HeaderCell>
                <Table.HeaderCell style={{ width: '10%' }}>
                  <div className="actions">
                    <div className="actions__icon"></div>
                    <div className="actions__icon"></div>
                    <div className="actions__icon">
                      <div className="actions__icon__pacs-sync-all-button">
                        <PACSSyncAllButton filters={filters} />
                      </div>
                    </div>
                    <div className="actions__icon"></div>
                  </div>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {studies.map((study) => (
                <StudyRow
                  key={study._id}
                  study={study}
                  thresholdForDisplay={thresholdForDisplay}
                  intl={intl}
                  multiSelected={multiSelectedStudies.includes(study._id)}
                  onMultiSelectChange={(checked: boolean) =>
                    setMultiSelectedStudies((prev) => {
                      if (checked) return [...prev, study._id];
                      return prev.filter((id) => id !== study._id);
                    })
                  }
                  selected={currentStudyID === study._id}
                  onRowClick={() => handleSelectRow(study._id)}
                  onStudyRemove={() => handleSelectStudyForRemoving(study._id)}
                />
              ))}
            </Table.Body>
          </Table>
        </InfiniteScroll>

        <div>
          <Confirm
            open={isAlertDeleteStudyOpen}
            header={intl.formatMessage({ id: 'monitoring.study.delete.header' })}
            content={intl.formatMessage({ id: 'monitoring.study.delete.content' })}
            confirmButton={
              <Button negative>
                <FormattedMessage id="general.confirm" />
              </Button>
            }
            cancelButton={intl.formatMessage({ id: 'general.cancel' })}
            onCancel={handleCancelDeleteStudy}
            onConfirm={deleteSelectedStudy}
          />
        </div>
      </div>
      <PreviewArea study={currentStudy} />
    </div>
  );
};

export default MonitoringPage;
